import React, { useImperativeHandle } from "react";
import { ActivityIndicator, View } from "react-native";
import { splitIntoChunks } from "../../helpers/common.helpers";
import {
  getImageData,
  pickImages,
} from "../../helpers/imagePicker/imagePicker";
import { Typography } from "common/components/Typography";
import { OutlinedButton } from "../buttons/OutlinedButton";
import UploadIcon from "../../assets/icons/upload.svg";
import colors from "../../styles/colors";
import { Asset } from "react-native-image-picker";
import { useMyPropertyPhotos } from "../../hooks/api.hooks";
import { isWeb } from "../../environment";
import { FormImage } from "../images/FormImage";
import CommonConfig from "../../commonConfig";
import { UploadedPhoto } from "../../types/common.types";
import { useTranslation } from "react-i18next";
import { showToastError, showToastWarning } from "../../toast/toast";
import { rotateAppraiserPropertyPhotos } from "web-valuations/src/api/appraiserProperties.api";
import { useMutation } from "react-query";

/** tested with jpg, webp, avif, png. Resulting rotated image is always png */
function rotateBase64({
  srcBase64,
  degrees,
  onSuccess,
  onError,
  onFinally,
}: {
  srcBase64: string;
  degrees: 90 | -90;
  onSuccess: (asset: { uri: string; width: number; height: number }) => void;
  onError: (reasonShort: string) => void;
  onFinally?: () => void;
}) {
  // https://gist.github.com/Zyndoras/6897abdf53adbedf02564808aaab94db
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const image = new Image();

  if (!ctx || !srcBase64) {
    onError(!ctx ? "cant canvas" : "empty str");
    onFinally?.();
    return;
  }

  image.onload = function () {
    canvas.width = degrees % 180 === 0 ? image.width : image.height;
    canvas.height = degrees % 180 === 0 ? image.height : image.width;

    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((degrees * Math.PI) / 180);
    ctx.drawImage(image, image.width / -2, image.height / -2);

    try {
      onSuccess({
        uri: canvas.toDataURL("image/png"),
        width: canvas.width,
        height: canvas.height,
      });
    } catch {
      // SecurityError: Failed to execute 'toDataURL' on 'HTMLCanvasElement': Tainted canvases may not be exported.
      onError("cant url to base64");
    } finally {
      onFinally?.();
    }
    canvas.remove();
    image.remove();
  };

  image.src = srcBase64;
}

interface FormGalleryProps {
  propertyId?: string;
  buttonBackgroundColor?: keyof typeof colors;
  onGalleryAdvicesPress?: () => void;
  getPhotosFunction?: (propertyId: string) => Promise<UploadedPhoto[]>;
}

export interface FormGalleryHandles {
  getUploadImages: () => FormData | undefined;
  getDeletedPhotosIds: () => string[];
}

const maxImageLimit = isWeb() ? 36 : 18;

export type BackendUri = {
  uri: string;
  backend_id: string;
};

export const FormGallery = React.forwardRef<
  FormGalleryHandles,
  FormGalleryProps
>(
  (
    {
      propertyId,
      buttonBackgroundColor,
      onGalleryAdvicesPress,
      getPhotosFunction,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const { data, isLoading, refetch } = useMyPropertyPhotos(
      propertyId,
      getPhotosFunction
    );
    const [selectedImages, setSelectedImages] = React.useState<Asset[]>([]);
    const [imagesToDelete, setImagesToDelete] = React.useState<string[]>([]);
    const [frozenRotateImages, setFrozenRotateImages] = React.useState<
      Asset["uri"][]
    >([]);
    const { mutate: rotateBackendImage } = useMutation({
      mutationFn: async ({
        photoId,
        degrees,
      }: {
        photoId: string;
        degrees: 90 | -90;
      }) => rotateAppraiserPropertyPhotos(photoId, degrees, propertyId),
    });

    const formattedImages = (() => {
      const savedImages =
        data
          ?.map((image) => ({
            uri: image.url,
            backend_id: image._id,
          }))
          .filter((image) => !imagesToDelete.includes(image.backend_id)) || [];
      return [...savedImages, ...selectedImages];
    })();

    const imageChunks = splitIntoChunks(2, formattedImages);

    const onGalleryUploadPress = async () => {
      const result = await pickImages(maxImageLimit - formattedImages.length);

      if (result.didCancel || !result.assets) {
        CommonConfig.loggingHandler("image_picker_cancelled");
        return;
      }
      CommonConfig.loggingHandler(
        `image_picker_success_${result.assets.length}`,
        {
          imagesCount: result.assets.length,
        }
      );
      setSelectedImages([...selectedImages, ...result.assets]);
    };

    const onCloseButtonPress = (imageInfo: Asset | BackendUri) => {
      const backend_id = (imageInfo as BackendUri).backend_id;
      if (backend_id !== undefined) {
        setImagesToDelete([...imagesToDelete, backend_id]);
      } else {
        setSelectedImages(
          selectedImages.filter((image) => image.uri !== imageInfo.uri)
        );
      }
    };

    const onRotateButtonPress = (imageInfo: Asset | BackendUri) => {
      const degrees = -90;
      const imageInfoKey = imageInfo.uri;
      if (frozenRotateImages.includes(imageInfoKey)) {
        showToastError(t("propertyCreation.gallery.rotateAlready"));
        return;
      }
      setFrozenRotateImages((prev) => [...prev, imageInfoKey]);
      const unfreezeImage = () =>
        setFrozenRotateImages((prev) =>
          prev.filter((key) => key !== imageInfoKey)
        );
      const backend_id = (imageInfo as BackendUri).backend_id;
      if (backend_id !== undefined) {
        rotateBackendImage(
          { photoId: backend_id, degrees },
          {
            onSettled: unfreezeImage,
            onSuccess: ({ rotatedByMe }) => {
              refetch();
              if (!rotatedByMe) {
                showToastWarning(
                  t("propertyCreation.gallery.rotateSomeoneElse")
                );
              }
            },
            onError: () => {
              showToastError(t("propertyCreation.gallery.rotateError"));
            },
          }
        );
      } else {
        // rotating inside frontend
        rotateBase64({
          srcBase64: imageInfo.uri ?? "",
          degrees,
          onSuccess: (newAsset) => {
            setSelectedImages((prev) =>
              prev.map((asset) =>
                asset.uri === imageInfo.uri ? newAsset : asset
              )
            );
          },
          onError: () => {
            showToastError(t("propertyCreation.gallery.rotateError"));
          },
          onFinally: unfreezeImage,
        });
      }
    };

    const getUploadImages = () => {
      if (!selectedImages.length) {
        return undefined;
      }
      return getImageData(selectedImages, "photos");
    };
    const getDeletedPhotosIds = () => {
      return imagesToDelete;
    };
    useImperativeHandle(ref, () => ({
      getUploadImages,
      getDeletedPhotosIds,
    }));

    return (
      <View>
        <View style={{ justifyContent: "space-between", flexDirection: "row" }}>
          <Typography variant="h3" textStyle={{ marginBottom: 10 }}>
            {t("propertyCreation.gallery.title")}
          </Typography>
          {!!onGalleryAdvicesPress && (
            <Typography
              variant="h4"
              textStyle={{ color: colors.purple2 }}
              onPress={onGalleryAdvicesPress}
            >
              {t("propertyCreation.gallery.getTips")}
            </Typography>
          )}
        </View>
        <Typography variant="caption" textStyle={{ fontSize: 16 }}>
          {t("propertyCreation.gallery.uploadLimit", { count: maxImageLimit })}
        </Typography>
        <View>{!!isLoading && <ActivityIndicator />}</View>

        <View style={{ marginHorizontal: -5, marginVertical: 10 }}>
          {isWeb() ? (
            <View style={{ flexDirection: "row", flexWrap: "wrap" }}>
              {formattedImages.map((image) => (
                <FormImage
                  key={image.uri}
                  uri={image?.uri ?? ""}
                  onCloseButtonPress={() => onCloseButtonPress(image)}
                  onRotateButtonPress={() => onRotateButtonPress(image)}
                />
              ))}
            </View>
          ) : (
            imageChunks.map((chunk, index) => (
              <View key={index} style={{ flexDirection: "row" }}>
                {chunk.map((image, index) => {
                  if (image === null) {
                    return <View key={index} style={{ flex: 1 }} />;
                  }
                  return (
                    <View style={{ flex: 1 }} key={index}>
                      <FormImage
                        uri={image?.uri ?? ""}
                        onCloseButtonPress={() => onCloseButtonPress(image)}
                        onRotateButtonPress={() => onRotateButtonPress(image)}
                      />
                    </View>
                  );
                })}
              </View>
            ))
          )}
        </View>
        <View style={{ maxWidth: 400, width: "100%", alignSelf: "center" }}>
          <OutlinedButton
            leftIcon={
              <View style={{ marginLeft: 10 }}>
                <UploadIcon />
              </View>
            }
            onPress={onGalleryUploadPress}
            backgroundColor={buttonBackgroundColor ?? "greybg"}
            style={{ borderColor: colors.purple2 }}
          >
            {t("propertyCreation.gallery.uploadPhotos")}
          </OutlinedButton>
        </View>
      </View>
    );
  }
);
